import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import AcronisIcon from '@liquidweb-dev/undercurrent/icons/platform/Acronis';
import LoaderCircleIcon from '@liquidweb-dev/undercurrent/icons/animated/LoaderCircle';
import Box from '@liquidweb-dev/undercurrent/Box';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import Button from '@liquidweb-dev/undercurrent/Button';
import type { DrawerProps } from '@liquidweb-dev/undercurrent/Drawer';
import DrawerHeader from '@liquidweb-dev/undercurrent/DrawerHeader';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import DrawerContent from '@liquidweb-dev/undercurrent/DrawerContent';
import DrawerFooter from '@liquidweb-dev/undercurrent/DrawerFooter';
import MeasureBar from '@liquidweb-dev/undercurrent/MeasureBar';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import TextField from '@liquidweb-dev/undercurrent/TextField';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import toast from '@liquidweb-dev/undercurrent/toast';
import {
	compareAcronisDestinations,
	getAcronisQuotaOptions,
	isAcronisCloudDestination,
} from 'modules/backups/helpers';
import {
	ASSET_DETAILS_QUERY_KEY,
	AssetDetails,
} from 'modules/queries/asset/useDetails';
import { ASSET_LIST_QUERY_KEY } from 'modules/queries/asset/useList';
import useProductFlatDetails from 'modules/queries/product/useFlatDetails';
import useCartItemAdd from 'modules/queries/market/cart/item/useAdd';
import useCartClaim from 'modules/queries/market/cart/useClaim';
import useCartOrder from 'modules/queries/market/cart/useOrder';
import {
	ACRONIS_EDR_ACTIVE_TITLE,
	ACRONIS_EDR_STORAGE_LOCATION_MESSAGE,
} from 'pages/servers/Backups/BackupsBlock/Settings/acronisEdrMessages';
import BackupSettingsQuotaSelect from 'pages/servers/Backups/BackupsBlock/Settings/QuotaSelect';
import BackupTile from 'pages/servers/Backups/BackupsBlock/BackupTile';
import acronisProductMap from 'utility/acronisProductMap';
import { useAssetBackupsUpsell } from 'modules/upsell/useUpsellState';
import useProductConfigIntent from 'modules/customerIntent/productConfiguration';
import useSendHubspotEvent from 'utility/gtm/hubspot/useEvent';

interface UpdateAcronisBackupSettingsProps extends DrawerProps {
	assetDetails: AssetDetails;
	onClose: () => void;
}

const AddAcronisBackupSettings = ({
	assetDetails,
	onClose,
}: UpdateAcronisBackupSettingsProps) => {
	const queryClient = useQueryClient();

	const [destination, setDestination] = useState<string | number>('');
	const [quotaOption, setQuotaOption] = useState<string | number>('');
	const [prevQuotaPtovId, setPrevQuotaPtovId] = useState<
		Record<number, number>
	>({});

	const {
		data: acronisProductDetails,
		isLoading: isAcronisProductDetailsLoading,
	} = useProductFlatDetails(
		{
			product_code: acronisProductMap(assetDetails?.type),
		},
		{ enabled: Boolean(assetDetails?.type) },
	);

	const { mutateAsync: addCartItem, isLoading: isAddingCartItem } =
		useCartItemAdd();
	const { mutateAsync: claimCart, isLoading: isClaimingCart } = useCartClaim();
	const { mutateAsync: orderCart, isLoading: isOrderingCart } = useCartOrder();

	const isPurchasing = isAddingCartItem || isClaimingCart || isOrderingCart;

	const acronisDestination = useMemo(
		() =>
			acronisProductDetails?.flat_options?.find(
				(item) => item.key === 'AcronisStorageDestination',
			),
		[acronisProductDetails],
	);

	const [_, setUpsellDismissed] = useAssetBackupsUpsell(assetDetails.uniq_id);

	const sendAddonEvent = useSendHubspotEvent('addon');

	const destinationOptions = useMemo(
		() =>
			(acronisDestination?.values || [])
				.filter((item) => {
					// Only offer destinations with at least one pickable quota.
					const destinationOptionValues = getAcronisQuotaOptions(
						acronisProductDetails?.flat_options?.find(
							(option) => option.parent_ptov_id === item.ptov_id,
						)?.values,
						assetDetails?.region_id,
					);

					return item.active && destinationOptionValues.length > 0;
				})
				.sort(compareAcronisDestinations),
		[acronisDestination, acronisProductDetails, assetDetails?.region_id],
	);

	const acronisCloudDestination = useMemo(
		() =>
			destinationOptions.find((item) => isAcronisCloudDestination(item.value)),
		[destinationOptions],
	);

	/** EDR servers must use Acronis Cloud; only lock when it's on offer so an option stays selectable. */
	const isLockedToAcronisCloud =
		Boolean(assetDetails?.backupsState?.hasAcronisEdr) &&
		Boolean(acronisCloudDestination);

	const quota = useMemo(
		() =>
			acronisProductDetails?.flat_options?.find(
				(item) => item?.parent_ptov_id === destination,
			),
		[acronisProductDetails, destination],
	);

	const quotaOptions = useMemo(
		() => getAcronisQuotaOptions(quota?.values, assetDetails?.region_id),
		[assetDetails?.region_id, quota?.values],
	);

	useEffect(() => {
		if (destinationOptions.length === 0) return;

		// Re-seed if a refetch dropped the selected destination.
		const isSelectionStillOffered = destinationOptions.some(
			(item) => item.ptov_id === destination,
		);
		if (destination === '' || !isSelectionStillOffered) {
			setDestination(destinationOptions[0].ptov_id);
			// The old quota belongs to the dropped destination; let the seeding effect pick a new one.
			setQuotaOption('');
		}
	}, [destinationOptions, destination]);

	useEffect(() => {
		if (
			quotaOption === '' &&
			destinationOptions.length > 0 &&
			quotaOptions.length > 0
		) {
			const value = quotaOptions[0].ptov_id;
			setQuotaOption(value);
			const destinationValue = destinationOptions[0].ptov_id;
			setPrevQuotaPtovId((prevState) => ({
				...prevState,
				[destinationValue]: value,
			}));
		}
	}, [destinationOptions, quotaOption, quotaOptions]);

	const productCode = acronisProductMap(assetDetails?.type);
	useProductConfigIntent(productCode);

	const handleSubmit = async () => {
		try {
			const cart = await addCartItem({
				cycle: assetDetails?.cycle,
				item: {
					code: productCode,
					options: [
						{
							parent_ptok_id: null,
							parent_ptov_id: null,
							ptok_id: Number(acronisDestination?.ptok_id),
							ptov_id: Number(destination),
							num_units: null,
						},
						{
							parent_ptok_id: Number(acronisDestination?.ptok_id),
							parent_ptov_id: Number(destination),
							ptok_id: Number(quota?.ptok_id),
							ptov_id: Number(quotaOption),
							num_units: null,
						},
					],
					properties: {
						related_subaccnt: assetDetails.uniq_id,
						domain: assetDetails.domain,
					},
					region_id: assetDetails.region_id,
				},
			});

			await claimCart({ uuid: cart.uuid });
			await orderCart({ uuid: cart.uuid });

			toast.success('Your purchase has been completed');

			queryClient.invalidateQueries({
				queryKey: [ASSET_DETAILS_QUERY_KEY, { uniq_id: assetDetails.uniq_id }],
			});
			queryClient.invalidateQueries({ queryKey: [ASSET_LIST_QUERY_KEY] });

			setUpsellDismissed(true);

			sendAddonEvent({
				serverUniqId: assetDetails.uniq_id,
				serverHostName: assetDetails.domain,
				addOnName: 'Acronis Backups',
			});

			onClose();
		} catch (_error) {
			// nothing
		}
	};

	const percentage = useMemo(() => {
		const selected = Number(
			quotaOptions?.find((item) => item.ptov_id === quotaOption)?.value || '0',
		);
		const max = Number(quotaOptions[quotaOptions.length - 1]?.value || '1');
		return (selected / max) * 100;
	}, [quotaOption, quotaOptions]);

	const applyDestination = (ptovId: number) => {
		setDestination(ptovId);

		const targetQuotaOptions = getAcronisQuotaOptions(
			acronisProductDetails?.flat_options?.find(
				(item) => item?.parent_ptov_id === ptovId,
			)?.values,
			assetDetails?.region_id,
		);

		// Restore the last quota picked here if still offered; never keep the previous destination's.
		const restoredQuota = targetQuotaOptions.find(
			(item) => item.ptov_id === prevQuotaPtovId[ptovId],
		);

		setQuotaOption(
			restoredQuota?.ptov_id ?? targetQuotaOptions[0]?.ptov_id ?? '',
		);
	};

	const handleDestinationChange = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		const ptovId = Number(event?.target?.value);
		const selected = destinationOptions.find((item) => item.ptov_id === ptovId);

		// Don't rely on the disabled MenuItem alone to enforce the EDR lock.
		if (isLockedToAcronisCloud && !isAcronisCloudDestination(selected?.value)) {
			return;
		}

		applyDestination(ptovId);
	};

	const handleQuotaOptionChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const value = Number(event.target.value);
		setQuotaOption(value);
		setPrevQuotaPtovId((prevState) => ({
			...prevState,
			[Number(destination)]: value,
		}));
	};

	return (
		<>
			<DrawerHeader
				onClose={onClose}
				description={
					<Typography
						variant="body-sm"
						sx={({ vars }) => ({ color: vars.palette.uc.text.medium })}
					>
						Configure your backup solution to protect your data
					</Typography>
				}
			>
				Backup settings
			</DrawerHeader>
			<DrawerContent sx={{ gap: 300 }}>
				<BackupTile
					label="Acronis Cyber Backups"
					description="Acronis Cloud delivers ultra-fast, secure, and flexible backup and recovery with military-grade encryption, unmatched speed, and complete control over all your data—anytime, anywhere."
					icon={
						<AcronisIcon
							sx={({ size }) => ({
								height: size.sm,
								width: size.sm,
							})}
						/>
					}
					sx={{ border: 'none', padding: 0 }}
				/>
				<Stack
					spacing={300}
					sx={{
						flex: 1,
					}}
				>
					{isAcronisProductDetailsLoading && (
						<Stack
							sx={{
								flex: 1,
								justifyContent: 'center',
								alignItems: 'center',
							}}
						>
							<LoaderCircleIcon
								sx={({ size }) => ({ height: size.lg, width: size.lg })}
							/>
						</Stack>
					)}
					{!isAcronisProductDetailsLoading && (
						<>
							<TextField
								name="destination"
								label="Select backup location"
								// Blank for the one render between a refetch dropping the selection and the re-seed.
								value={
									destinationOptions.some(
										(item) => item.ptov_id === destination,
									)
										? destination
										: ''
								}
								onChange={handleDestinationChange}
								disabled={isPurchasing}
								slotProps={{
									select: {
										// Keep "(Recommended)" out of the closed field, per Figma.
										renderValue: (value: unknown) =>
											destinationOptions.find(
												(item) => item.ptov_id === Number(value),
											)?.description ?? '',
									},
								}}
								fullWidth
								select
							>
								{destinationOptions?.map((item) => {
									const isAcronisCloud = isAcronisCloudDestination(item.value);
									const isLockedOut = isLockedToAcronisCloud && !isAcronisCloud;

									const label = (
										<>
											{item?.description}
											{isAcronisCloud && (
												<>
													{' '}
													<Box
														component="span"
														sx={({ vars, size }) => ({
															color: vars.palette.uc.success.text.main,
															fontSize: size.xS,
														})}
													>
														(Recommended)
													</Box>
												</>
											)}
										</>
									);

									return (
										<MenuItem
											key={item.ptov_id}
											value={item.ptov_id}
											disabled={isLockedOut}
										>
											{isLockedOut ? (
												<Tooltip
													arrow
													placement="bottom"
													variant="fancy"
													title={ACRONIS_EDR_ACTIVE_TITLE}
													message={ACRONIS_EDR_STORAGE_LOCATION_MESSAGE}
												>
													{/* Disabled items ignore pointer events; re-enable them for the tooltip only. */}
													<Box
														component="span"
														sx={{
															display: 'flex',
															flex: 1,
															pointerEvents: 'auto',
														}}
													>
														{label}
													</Box>
												</Tooltip>
											) : (
												label
											)}
										</MenuItem>
									);
								})}
							</TextField>
							<Stack direction="row" spacing={200}>
								<MeasureBar
									orientation="vertical"
									percentage={percentage}
									sx={{
										height: 1,
										minWidth: 72,
									}}
									hidePercentage
								/>
								{acronisProductDetails && (
									<BackupSettingsQuotaSelect
										acronisProductDetails={acronisProductDetails}
										assetDetails={assetDetails}
										destination={destination}
										disabled={isPurchasing}
										onChange={handleQuotaOptionChange}
										value={quotaOption}
									/>
								)}
							</Stack>
						</>
					)}
				</Stack>
			</DrawerContent>
			<DrawerFooter spacing={100}>
				<Button
					variant="primary"
					color="highlight"
					onClick={handleSubmit}
					loading={isPurchasing}
					disabled={
						isAcronisProductDetailsLoading ||
						// Nothing on offer in this region.
						destination === '' ||
						quotaOption === ''
					}
					size="large"
				>
					Buy now
				</Button>
				<Button
					variant="ghost"
					onClick={onClose}
					disabled={isPurchasing}
					size="large"
				>
					Cancel
				</Button>
			</DrawerFooter>
		</>
	);
};

export default AddAcronisBackupSettings;
